import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { api } from '../services/api'
import './Toast.css'

const CartContext = createContext(null)

function reducer(items, action) {
  switch (action.type) {
    case 'add': {
      const found = items.find(i => i.id === action.item.id)
      return found
        ? items.map(i => i.id === found.id ? { ...i, qty: i.qty + 1 } : i)
        : [...items, { ...action.item, qty: 1 }]
    }
    case 'dec': return items.flatMap(i => i.id !== action.id ? [i] : i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : [])
    case 'remove': return items.filter(i => i.id !== action.id)
    case 'clear': return []
    default: return items
  }
}

const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) || fallback } catch { return fallback } }

export function CartProvider({ children }) {
  const [items, rawDispatch] = useReducer(reducer, [], () => read('ipoint-cart', []))
  const [wish, setWish] = useState(() => read('ipoint-wish', []))
  const [open, setCartOpen] = useState(false)       // cart drawer
  const [wishOpen, setWishOpenState] = useState(false) // wishlist slide-in panel
  const [checkout, setCheckout] = useState(false)   // checkout modal
  const [toast, setToast] = useState(null)          // { id, text, action }
  const timer = useRef(0)

  useEffect(() => { localStorage.setItem('ipoint-cart', JSON.stringify(items)) }, [items])
  useEffect(() => { localStorage.setItem('ipoint-wish', JSON.stringify(wish)) }, [wish])
  useEffect(() => () => clearTimeout(timer.current), [])

  // once per page load: drop saved ids whose product was deleted or hidden in the admin,
  // so the wishlist badge and the wishlist panel always agree
  useEffect(() => {
    let alive = true
    api.list('products').then(d => {
      if (!alive || !Array.isArray(d)) return
      const live = new Set(d.filter(x => x.is_active == null || Number(x.is_active)).map(x => String(x.id)))
      setWish(w => { const kept = w.filter(id => live.has(String(id))); return kept.length === w.length ? w : kept })
    }).catch(() => {}) // server unreachable: keep the list as it is
    return () => { alive = false }
  }, [])

  // small message at the bottom of the screen; `action` adds a link-style button ({ label, run })
  const notify = useCallback((text, action) => {
    clearTimeout(timer.current)
    setToast({ id: Date.now(), text, action })
    timer.current = setTimeout(() => setToast(null), 3500)
  }, [])

  // opening the cart or the wishlist closes the other one and hides the message
  const setOpen = useCallback(v => { setCartOpen(v); if (v) { setWishOpenState(false); setToast(null) } }, [])
  const setWishOpen = useCallback(v => { setWishOpenState(v); if (v) { setCartOpen(false); setToast(null) } }, [])

  // every "add" anywhere on the site shows "added to cart"
  const dispatch = useCallback(action => {
    rawDispatch(action)
    if (action.type === 'add') notify(`${action.item.name} added to cart`, { label: 'View cart', run: () => setOpen(true) })
  }, [notify, setOpen])

  const toggleWish = useCallback(id => {
    const had = wish.includes(id)
    setWish(w => w.includes(id) ? w.filter(x => x !== id) : [...w, id])
    notify(had ? 'Removed from wishlist' : 'Saved to wishlist', had ? undefined : { label: 'View wishlist', run: () => setWishOpen(true) })
  }, [wish, notify, setWishOpen])

  const value = useMemo(() => ({
    items, open, setOpen, wishOpen, setWishOpen, checkout, setCheckout, dispatch, wish, toggleWish, notify,
    count: items.reduce((n, i) => n + i.qty, 0),
    total: items.reduce((n, i) => n + i.qty * i.price, 0),
  }), [items, open, setOpen, wishOpen, setWishOpen, checkout, dispatch, wish, toggleWish, notify])

  return (
    <CartContext.Provider value={value}>
      {children}
      {toast && (
        <div className="toast" role="status" key={toast.id}>
          <span className="toast__tick" aria-hidden="true">✓</span>
          <span className="toast__text">{toast.text}</span>
          {toast.action && <button type="button" className="toast__act" onClick={toast.action.run}>{toast.action.label}</button>}
          <button type="button" className="toast__x" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}
    </CartContext.Provider>
  )
}

export const useCart = () => useContext(CartContext)